import type { ReactNode } from "react";

import { cx } from "class-variance-authority";
import type { TooltipProps } from "recharts";

import * as tooltipTheme from "../Tooltip/Tooltip.css";

import * as theme from "./GroupedBarChart.css";
import { GroupedBarChartBar, GroupedBarChartSeries } from "./types";

type RechartsTooltipProps = Pick<
    TooltipProps<number, string>,
    "active" | "payload" | "label"
>;

export interface GroupedBarChartTooltipProps extends RechartsTooltipProps {
    series: GroupedBarChartSeries[];
    renderContent?: (bar: GroupedBarChartBar) => ReactNode;
}

export function GroupedBarChartTooltip({
    series,
    renderContent,
    active,
    label,
    payload,
}: GroupedBarChartTooltipProps) {
    const entry = payload?.[0];
    const hovered = series.find(({ key }) => key === entry?.dataKey);
    const value = entry?.value ?? 0;

    // recharts still draws a zero bar, but there is no mark to point at.
    if (active !== true || hovered === undefined || value === 0) {
        return null;
    }

    // Item tooltips carry the hovered row; the axis label can be stale.
    const row: unknown = entry?.payload;
    const bucketLabel =
        typeof row === "object" &&
        row !== null &&
        "groupedBarChartBucketLabel" in row
            ? row.groupedBarChartBucketLabel
            : label;
    const bar: GroupedBarChartBar = {
        bucketLabel: String(bucketLabel ?? ""),
        series: hovered,
        value,
    };

    // Always wrap: `renderContent` replaces the content, not the card.
    return (
        <div className={cx(tooltipTheme.content, theme.tooltip)}>
            {renderContent !== undefined ? (
                renderContent(bar)
            ) : (
                <>
                    <span className={theme.tooltipLabel}>
                        {bar.bucketLabel}
                    </span>
                    <span className={theme.tooltipRow}>
                        <span>{bar.series.label}</span>
                        <span>{bar.value}</span>
                    </span>
                </>
            )}
        </div>
    );
}
